// Super_admin-only top-level page for onboarding a brand-new station.
// Lives at /admin/onboard. Pulled OUT of AdminShell because onboarding
// creates a new station and isn't scoped to any current one.
//
// Auth:
//   • signed out                       → redirect to /admin (single sign-in surface)
//   • signed in, not super_admin       → redirect to /admin (picker)
//   • signed in, super_admin           → render the onboarding form
//
// On successful onboard, navigate to /admin/<newslug> so the user drops
// into the freshly-created station's admin shell.

import React from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import PublisherForm, { type PublisherFormPayload } from '../../components/self-service/admin/PublisherForm';
import { usePublisherFormSubmit } from '../../components/self-service/admin/usePublisherFormSubmit';
import { useAuth } from '../../components/self-service/auth/useAuth';

const PublisherOnboardPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const submitCreate = usePublisherFormSubmit('selfservice-create-publisher');

  if (!isAuthenticated || !user) {
    // Single sign-in surface lives at /admin. Carry the deep-link target
    // so AdminEntryPage can route the user back here after auth completes.
    return (
      <Navigate
        to="/admin"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  if (user.rawRole !== 'super_admin') {
    return <Navigate to="/admin" replace />;
  }

  const handleCreate = async (payload: PublisherFormPayload) => {
    const result = await submitCreate(payload);
    setTimeout(() => navigate(`/admin/${result.slug}`), 600);
    return result;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 py-10 md:py-14">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </Link>
        <div className="mb-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 text-white text-[10px] font-semibold uppercase tracking-[0.14em] mb-4">
            <Sparkles className="w-3 h-3" />
            New channel
          </div>
          <h1 className="text-[28px] md:text-[36px] font-semibold tracking-tight text-slate-900 leading-tight">
            Onboard a channel
          </h1>
          <p className="mt-3 text-[15px] text-slate-500 leading-relaxed max-w-xl">
            Paste the channel's website URL and click{' '}
            <span className="font-medium text-slate-700">Extract branding</span> to pre-fill
            the logo, colors, font, and DMA. Review the rest before saving.
          </p>
        </div>
        <PublisherForm mode="create" initial={null} onSubmit={handleCreate} />
      </div>
    </div>
  );
};

export default PublisherOnboardPage;
